import {LockClosedIcon} from '@heroicons/react/solid';
import {isEmpty} from 'lodash-es';
import React from 'react';

import {Button, CheckBox, Input, Select, SpinningLoader} from '@/components';
import {NumberInput} from '@/components/common/Input';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import {FeePredicateInput} from '@/screens/Merchants/Fee/FeePredicateInput';
import ArbitrageDisplay from '@/screens/Rates/Upload/ArbitrageDisplay';
import type {
  AfriexRate,
  AfriexRatePromo,
  ArbitrageCheckInfo,
  UserType,
} from '@/types';
import {AfriexRatePromoCode, BusinessFeeField} from '@/types';
import {formatValues} from '@/utils/dashboard';
import {logger} from '@/utils/logger';
import {applyDiscountToRates} from '@/utils/rates/applyDiscountToRates';
import {convertRatesListToMap} from '@/utils/rates/convertRatesListToMap';
import {toTitleCase} from '@/utils/toTitleCase';

import RateWidget from './RateWidget';
import {TierActionType} from './types';
import useTierUpdate from './useTierUpdate';
import {
  convertFeeTagToPromoTags,
  convertPromoTagsToBusinessFeeTags,
  extractCurrenciesFromTags,
} from './utils';

interface TierFormModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  initialData?: AfriexRatePromo;
  rawAppRates?: AfriexRate[];
  target?: UserType;
  businessId?: string;
  businessName?: string;
  /** Test hook prefix; each rendered field namespaces under it. */
  formDataCy?: string;
  /** Called after a successful create/update so the list can refresh in place. */
  onSuccess?: () => void | Promise<void>;
}

const TierFormModal = ({
  isOpen,
  onClose,
  initialData,
  rawAppRates,
  target,
  businessId,
  businessName,
  formDataCy,
  onSuccess,
}: TierFormModalProps) => {
  // Pre-seed a new promo's target — and, from within a merchant profile, the
  // scoping tag — so the reducer's state always carries the correct target
  // from construction. Mirrors FeeFormModal seeding `tags` once up front
  // rather than resolving it downstream at submit time. Editing an existing
  // promo needs no seeding: initialData already carries its stored target.
  const seededInitialData = isEmpty(initialData)
    ? ({
        target,
        ...(businessId
          ? {
              meta: {
                tags: {BUSINESSID: `EQUALS::${businessId}`},
                flags: {isDeactivated: false},
              },
            }
          : {}),
      } as AfriexRatePromo)
    : initialData;
  const {
    arbitrageResult,
    isArbitrageDetected,
    isArbitrageChecked,
    handleToleranceChange,
    checkIfRateTierHasArbitrage,
    dispatch,
    isLoading,
    handleSubmit,
    shouldShowArbitrageTable,
    setShouldShowArbitrageTable,
    tier,
    tolerance,
  } = useTierUpdate(onClose, seededInitialData, rawAppRates, target, onSuccess);

  const {discount, priority, name, code} = tier;
  const isActive = !tier?.meta?.flags?.isDeactivated;
  const shouldSkipFee = tier?.meta?.flags?.shouldSkipFee;
  const shouldIgnoreStickiness = tier?.meta?.flags?.shouldIgnoreStickiness;
  const createTitle =
    target === 'BUSINESS' ? 'New Business Promo +' : 'New Promo Rate +';

  const updateTitle =
    target === 'BUSINESS' ? 'Update Business Promo' : 'Update Promo Tier';
  const updateLabel = isEmpty(initialData) ? createTitle : updateTitle;
  const submitLabel = isEmpty(initialData) ? 'Submit' : 'Update';
  const isDisabled = isEmpty(tier);

  const codeSelectionOptions = [
    {value: '', key: '--Select Code--'},
    ...Object.values(AfriexRatePromoCode).map((code) => ({
      value: code,
      key: toTitleCase(code?.replace('_', ' ')),
    })),
  ];

  const discountedRates = applyDiscountToRates(
    rawAppRates ?? [],
    tier.discount ?? 0,
    extractCurrenciesFromTags(tier),
  );
  const discountedRatesMap = convertRatesListToMap(discountedRates);

  const shouldShowArbitrageInfo =
    isArbitrageDetected && isArbitrageChecked && shouldShowArbitrageTable;
  const arbitrageButtonLabel = shouldShowArbitrageInfo
    ? 'Hide Arbitrage Info'
    : 'Check Arbitrage';
  const arbitrageButtonHandler = shouldShowArbitrageInfo
    ? () => setShouldShowArbitrageTable(false)
    : () => checkIfRateTierHasArbitrage();

  const discountInPercentage = formatValues((discount || 0) * 100, 'rate', 6);
  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-1 sm:px-0"
        height="h-4/5"
        closeOnOutsideClick
        title={updateLabel}
        dataCy={formDataCy}
      >
        <div className="px-4 pt-3 overflow-y-hidden">
          {arbitrageResult && arbitrageResult?.currencies?.length && (
            <ArbitrageDisplay
              isOpen={
                arbitrageResult && arbitrageResult?.currencies?.length > 0
              }
              arbitrageResult={arbitrageResult}
              rates={discountedRatesMap}
              tolerance={tolerance}
            />
          )}

          {isLoading ? (
            <div className="flex w-full h-full justify-center items-center my-5">
              <SpinningLoader />
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // `handleSubmit` already reports a failed promo-tier save to
                // the admin through showErrorMessage, so this only fires if
                // that reporting itself threw. Dev logger rather than a second
                // toast: a toast is what just failed.
                handleSubmit(tier).catch(logger.error);
              }}
              className="flex flex-col space-y-5"
            >
              {businessId ? (
                <div className="flex items-center justify-between rounded-lg border border-green-500/60 bg-green-500/10 px-3 py-2">
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Applies to merchant
                    </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {businessName || businessId}
                    </span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                    <LockClosedIcon className="h-3.5 w-3.5" />
                    scoped
                  </span>
                </div>
              ) : null}

              {discount && (
                <RateWidget
                  rates={rawAppRates ?? []}
                  discount={discount ?? 0}
                />
              )}

              <Input
                label="Name"
                dataCy={formDataCy ? `${formDataCy}-name` : undefined}
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: TierActionType.UPDATE_TIER,
                    payload: {
                      name: e.target?.value,
                    },
                  })
                }
                className="w-full"
              />

              <Select
                label="Code"
                dataCy={formDataCy ? `${formDataCy}-code` : undefined}
                value={code}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  dispatch({
                    type: TierActionType.UPDATE_TIER,
                    payload: {
                      code: e.target?.value,
                    },
                  })
                }
                name="role"
                options={codeSelectionOptions}
              />

              <Input
                label={
                  <span>
                    Discount value (
                    <span
                      className={`font-semibold text-sm ${Number(discountInPercentage) >= 100 ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}
                    >
                      {discountInPercentage}%
                    </span>
                    )
                  </span>
                }
                min="-1"
                max="1"
                step="0.0001"
                type="number"
                dataCy={formDataCy ? `${formDataCy}-discount` : undefined}
                value={discount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: TierActionType.UPDATE_TIER,
                    payload: {
                      discount: e.target?.value
                        ? Number(e.target?.value)
                        : discount,
                    },
                  })
                }
                className="w-full"
              />

              <NumberInput
                label="Priority"
                min="0"
                dataCy={formDataCy ? `${formDataCy}-priority` : undefined}
                value={priority}
                onValueChange={(rawvalue) => {
                  dispatch({
                    type: TierActionType.UPDATE_TIER,
                    payload: {
                      priority: rawvalue ? Number(rawvalue) : priority,
                    },
                  });
                }}
                className="w-full"
              />

              <div className="mb-2">
                <div className="w-full mb-2">
                  <FeePredicateInput
                    tags={convertPromoTagsToBusinessFeeTags(
                      tier?.meta?.tags ?? {},
                    )}
                    businessId={businessId}
                    onRemove={(tag: string) => {
                      dispatch({
                        type: TierActionType.REMOVE_TAG,
                        payload: tag,
                      });
                    }}
                    onChange={(promoTag: string) => {
                      dispatch({
                        type: TierActionType.UPDATE_TAGS,
                        payload: convertFeeTagToPromoTags(promoTag),
                      });
                    }}
                    dataCy={formDataCy ? `${formDataCy}-predicate` : undefined}
                    fieldsToOmit={[
                      BusinessFeeField.ToProcessor,
                      BusinessFeeField.FromProcessor,
                      // WalletTag filters only apply to business-targeted
                      // promos; hide the field for AFRIEX promos.
                      ...(target !== 'BUSINESS'
                        ? [BusinessFeeField.WalletTag]
                        : []),
                    ]}
                  />
                </div>
              </div>

              <CheckBox
                label="Skip Fee"
                type="checkbox"
                dataCy={formDataCy ? `${formDataCy}-skip-fee` : undefined}
                checked={Boolean(shouldSkipFee)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: TierActionType.UPDATE_FLAGS,
                    payload: {
                      shouldSkipFee: Boolean(e.target?.checked),
                    },
                  })
                }
              />
              <CheckBox
                label="Disable Stickiness"
                type="checkbox"
                dataCy={
                  formDataCy ? `${formDataCy}-disable-stickiness` : undefined
                }
                checked={Boolean(shouldIgnoreStickiness)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: TierActionType.UPDATE_FLAGS,
                    payload: {
                      shouldIgnoreStickiness: Boolean(e.target?.checked),
                    },
                  })
                }
              />
              <CheckBox
                label="Active"
                type="checkbox"
                dataCy={formDataCy ? `${formDataCy}-active` : undefined}
                checked={Boolean(isActive)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch({
                    type: TierActionType.UPDATE_FLAGS,
                    payload: {
                      isDeactivated: !e.target?.checked,
                    },
                  })
                }
              />

              {shouldShowArbitrageInfo && (
                <ArbitrageDisplay
                  isOpen={shouldShowArbitrageInfo}
                  onClose={arbitrageButtonHandler}
                  arbitrageResult={arbitrageResult as ArbitrageCheckInfo}
                  rates={discountedRatesMap}
                  tolerance={tolerance}
                  onToleranceChange={handleToleranceChange}
                />
              )}

              <div className="flex w-full justify-end space-x-3 py-5">
                {!shouldShowArbitrageInfo && (
                  <Button
                    type="button"
                    onClick={arbitrageButtonHandler}
                    variant="outline"
                    colorScheme="cyan"
                    dataCy={formDataCy ? `${formDataCy}-arbitrage` : undefined}
                  >
                    {arbitrageButtonLabel}
                  </Button>
                )}

                <Button
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                  dataCy={formDataCy ? `${formDataCy}-submit` : undefined}
                >
                  {submitLabel}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default TierFormModal;
